import {
  applyPlayerCommand as applyCoreCommand,
  DEVICE_CMD,
  type CommandOutcome,
  type RealtimeCommand,
} from "@workspace/player-core";
import type { Playback } from "@/player/hooks/use-playback";
import { parseMediaRef } from "@/player/lib/resolve-media";

/** Alias for player-side command handling — same strings as DEVICE_CMD. */
export const PLAYER_COMMAND = DEVICE_CMD;

export type PlayerCommandName =
  (typeof PLAYER_COMMAND)[keyof typeof PLAYER_COMMAND];

/** Destructive commands — gateway requires manage.fleet (see ws-message-security-matrix). */
export const DESTRUCTIVE_PLAYER_COMMANDS = new Set([
  DEVICE_CMD.quit,
  DEVICE_CMD.logout,
  DEVICE_CMD.clearCache,
  DEVICE_CMD.restart,
]);

/** Best-effort wipe of cached media/schedule (Cache Storage + IndexedDB). */
export async function clearAppCaches(): Promise<void> {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore — clearing is best-effort
  }
  try {
    const idb = window.indexedDB;
    if (idb && "databases" in idb) {
      const dbs = await idb.databases();
      await Promise.all(
        dbs.map(
          (d) =>
            d.name &&
            new Promise<void>((resolve) => {
              const req = idb.deleteDatabase(d.name as string);
              req.onsuccess = req.onerror = req.onblocked = () => resolve();
            }),
        ),
      );
    }
  } catch {
    // ignore
  }
}

interface CommandHelpers {
  forceSync: () => void;
  quit?: () => void;
  logout?: () => void;
}

export async function applyPlayerCommand(
  playback: Playback,
  cmd: RealtimeCommand,
  helpers: CommandHelpers,
): Promise<CommandOutcome> {
  // A live volume/mute adjustment must briefly shield the player from
  // settings-driven re-applies: an in-flight /device/sync payload fetched
  // BEFORE the server persisted this command would otherwise stomp the fresh
  // level back to its stale value (see the persisted-output effect).
  if (cmd.command === "set_volume" || cmd.command === "set_mute") {
    playback.noteRemoteOutputChange();
  }
  return applyCoreCommand(playback, cmd, {
    forceSync: helpers.forceSync,
    quit: helpers.quit,
    logout: helpers.logout,
    parseMediaRef,
    onRestart: () => {
      window.setTimeout(() => window.location.reload(), 150);
    },
    onClearCache: async () => {
      await clearAppCaches();
      window.setTimeout(() => window.location.reload(), 150);
    },
  });
}

export type { CommandOutcome, RealtimeCommand };
